import actions from "./actions";
import CircuitsStateRecord from "@infinode/records/reducers/CircuitsStateRecord";
import { createCircuitRecord } from "@infinode/records/CircuitRecord";
import { CIRCUIT_TYPES } from "src/infinode/constants/circuit";
import zonesActions from "../zones/actions";
import { Set, OrderedMap } from "immutable";
import reducerUtils from "../reducerUtils";
import { createCircuitCategoryRecord } from "@infinode/records/CircuitCategoryRecord";

const updateCircuitsMap = (circuits, newCircuits, zoneId) => {
  return circuits.withMutations(circuitsMutator => {
    newCircuits.forEach(c => {
      const circuitRecord = createCircuitRecord(c, zoneId);
      circuitsMutator.set(c.id, circuitRecord);
    });
  });
};

const updateCircuitsListByZone = (circuitsByZoneMap, newCircuits, zoneId) => {
  return circuitsByZoneMap.set(zoneId, Set(newCircuits.map(z => z.id)));
};

const getCircuits = (state, newCircuits, zoneId) => {
  return state.merge({
    loading: false,
    circuits: updateCircuitsMap(state.circuits, newCircuits, zoneId),
    circuitIdsByZoneId: updateCircuitsListByZone(state.circuitIdsByZoneId, newCircuits, zoneId)
  });
};

const updateCircuitByZone = (circuitsByZoneMap, circuit, zoneId) => {
  const circuitIdsList = circuitsByZoneMap.get(zoneId) || Set();
  return circuitsByZoneMap.set(zoneId, circuitIdsList.add(circuit.id));
};

const updateCircuitMap = (circuits, newCircuit, zoneId) => {
  return circuits.set(newCircuit.id, createCircuitRecord(newCircuit, zoneId));
};

const getCircuit = (state, newCircuit, zoneId) => {
  return state.merge({
    loading: false,
    circuits: updateCircuitMap(state.circuits, newCircuit, zoneId),
    circuitIdsByZoneId: updateCircuitByZone(state.circuitIdsByZoneId, newCircuit, zoneId)
  });
};

const deleteCircuitIdFromZone = (circuitIdsByZoneId, circuitId, zoneId) => {
  return circuitIdsByZoneId.update(zoneId, set => (set ? set.delete(circuitId) : set));
};

/**
 * Make sure only one main and one end circuit in one zone
 * @param {Map} circuits
 * @param {object} newCircuit
 * @param {string} zoneId
 * @returns
 */
const updateCircuitWithTypeCheck = (circuits, newCircuit, zoneId) => {
  let newCircuits = updateCircuitMap(circuits, newCircuit, zoneId);
  if (newCircuit.circuitType !== CIRCUIT_TYPES.NORMAL) {
    const { circuitType, id } = newCircuit;
    newCircuits = newCircuits.map(c =>
      c.id === id || c.zoneId !== zoneId || c.circuitType !== circuitType
        ? c
        : c.set("circuitType", CIRCUIT_TYPES.NORMAL)
    );
  }
  return newCircuits;
};

const updateCircuitCategories = categories => {
  const map = OrderedMap().withMutations(mm => {
    Object.keys(categories).forEach(catLevelKey => {
      const level = catLevelKey.endsWith("1") ? 0 : 1;
      const catsArr = categories[catLevelKey];
      catsArr.forEach(catObj => {
        mm.set(catObj.key, createCircuitCategoryRecord(catObj, level));
      });
    });
  });
  return map;
};

export default function circuitsReducer(state = CircuitsStateRecord(), action) {
  const { circuits, circuit, zoneId } = action;
  switch (action.type) {
    case actions.GET_CIRCUITS_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.GET_CIRCUITS_SUCCESS:
      return getCircuits(state, circuits, zoneId);
    case actions.GET_CIRCUIT_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.GET_CIRCUIT_SUCCESS:
      return getCircuit(state, circuit, zoneId);
    case actions.DELETE_CIRCUIT_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.DELETE_CIRCUIT_SUCCESS:
      // TODO: what if delete main/end circuit?
      return state.merge({
        loading: false,
        circuits: state.circuits.delete(action.id),
        circuitIdsByZoneId: deleteCircuitIdFromZone(state.circuitIdsByZoneId, action.id, zoneId)
      });
    case actions.UPDATE_CIRCUIT_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.UPDATE_CIRCUIT_SUCCESS:
      return state.merge({
        loading: false,
        circuits: updateCircuitWithTypeCheck(state.circuits, circuit, zoneId)
      });
    case actions.CREATE_CIRCUIT_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.CREATE_CIRCUIT_SUCCESS:
      return state.merge({
        loading: false,
        circuits: updateCircuitWithTypeCheck(state.circuits, circuit, zoneId),
        circuitIdsByZoneId: updateCircuitByZone(state.circuitIdsByZoneId, circuit, zoneId)
      });
    case actions.GET_CIRCUIT_CATEGORIES_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.GET_CIRCUIT_CATEGORIES_SUCCESS:
      return state.merge({
        loading: false,
        circuitCategories: updateCircuitCategories(action.categories)
      });
    case actions.UPDATE_CIRCUIT_DEMAND_CONFIG_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.UPDATE_CIRCUIT_DEMAND_CONFIG_SUCCESS:
      return state.withMutations(s => {
        s.merge({ loading: false, error: null });
        s.updateIn(["circuits", action.circuitId], s =>
          s.merge({ demandOn: action.demandSwitch, demandType: action.demandType, demandInterval: action.interval })
        );
      });
    case actions.CIRCUITS_ERROR:
      return state.merge({ loading: false, error: action.error });
    // Zone action
    case zonesActions.DELETE_ZONE_SUCCESS:
      return state.update("circuitIdsByZoneId", map => map.set(action.id, Set()));
    default:
      return state;
  }
}
